/**
 * Shareable deep links to a single message.
 *
 *   guild channel : /channels/{guildId}/{channelId}/{messageId}
 *   DM / group DM : /dm/{channelId}/{messageId}
 *
 * A message link is a pure POINTER — it never grants access. Opening one
 * jumps to the message if the viewer already has access; otherwise the app
 * tells them they need an invite (see useMessageDeepLink). Bringing someone
 * onto a server is a separate, explicit action ("Пригласить на сервер") that
 * mints a scoped invite — so a shared message link can't double as an open
 * server entrance.
 *
 * The app routes everything through `*` → HomePage (no per-channel routes),
 * so on load HomePage parses the path with {@link parseMessageLink}, jumps to
 * the channel + scrolls to the message, then cleans the URL back to "/".
 */

export interface MessageLinkTarget {
  kind: 'guild' | 'dm';
  guildId?: string;
  channelId: string;
  messageId: string;
}

/** Build an absolute shareable link. `origin` defaults to the current page. */
export function buildMessageLink(
  target: { guildId?: string | null; channelId: string; messageId: string },
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string {
  const path = target.guildId
    ? `/channels/${target.guildId}/${target.channelId}/${target.messageId}`
    : `/dm/${target.channelId}/${target.messageId}`;
  return `${origin}${path}`;
}

const CUID = '[a-z0-9]+';
const GUILD_RE = new RegExp(`^/channels/(${CUID})/(${CUID})/(${CUID})/?$`);
const DM_RE = new RegExp(`^/dm/(${CUID})/(${CUID})/?$`);

/** Parse a pathname into a message target, or null if it isn't a message link. */
export function parseMessageLink(pathname: string): MessageLinkTarget | null {
  const g = GUILD_RE.exec(pathname);
  if (g) return { kind: 'guild', guildId: g[1], channelId: g[2]!, messageId: g[3]! };
  const d = DM_RE.exec(pathname);
  if (d) return { kind: 'dm', channelId: d[1]!, messageId: d[2]! };
  return null;
}
